<template>
  <div class="metadata-filter-editor">
    <p v-if="!availableLists.length" class="hint">No {{ scope }} metadata available to filter on.</p>
    <template v-else>
      <label class="field-label" :for="`${idPrefix}-lists`">{{ scopeLabel }} metadata templates</label>
      <multiselect
        :id="`${idPrefix}-lists`"
        :model-value="selectedLists"
        :options="availableLists"
        :multiple="true"
        :close-on-select="false"
        label="name"
        track-by="id"
        :aria-label="`${scopeLabel} metadata templates`"
        placeholder="Select relevant templates"
        @update:model-value="onListsChange"
      />
      <p v-if="!selectedLists.length" class="hint">Select templates to choose their keys. Selecting a template alone does not filter results.</p>
    </template>

    <ul class="md-rows">
      <li v-for="(row, index) in rows" :key="row.uid" class="condition">
        <span v-if="index" class="and-label">AND</span>
        <div class="md-row">
          <multiselect
            :id="`${idPrefix}-${row.uid}-key`"
            :model-value="row.key"
            :options="keyGroups"
            group-values="keys"
            group-label="listName"
            :group-select="false"
            :custom-label="keyLabel"
            track-by="id"
            :allow-empty="false"
            placeholder="Select key"
            :aria-label="`${scopeLabel} metadata key ${index + 1}`"
            @update:model-value="onKeyChange(index, $event)"
          />
          <template v-if="row.key">
            <select
              :value="row.operator"
              :aria-label="`${scopeLabel} ${row.key.name} operator`"
              @change="updateRow(index, { operator: $event.target.value, value2: '' })"
            >
              <option v-for="op in row.key.operators" :key="op" :value="op">{{ operatorLabel(op) }}</option>
            </select>
            <div v-if="valueFields(row).length" class="value-inputs">
              <template v-for="field in valueFields(row)" :key="field">
                <select
                  v-if="row.key.value_type === 'boolean'"
                  :value="row[field]"
                  :aria-label="valueAria(row, field)"
                  :aria-invalid="rowError(row) ? 'true' : null"
                  :aria-describedby="rowError(row) ? `${idPrefix}-${row.uid}-error` : null"
                  @change="updateRow(index, { [field]: $event.target.value })"
                >
                  <option value="">Select…</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <input
                  v-else
                  :type="inputType(row.key.value_type)"
                  :step="row.key.value_type === 'float' ? 'any' : null"
                  :value="row[field]"
                  :aria-label="valueAria(row, field)"
                  :aria-invalid="rowError(row) ? 'true' : null"
                  :aria-describedby="rowError(row) ? `${idPrefix}-${row.uid}-error` : null"
                  @input="updateRow(index, { [field]: $event.target.value })"
                />
              </template>
              <span v-if="row.key.unit" class="unit">{{ row.key.unit }}</span>
            </div>
            <p v-if="scope === 'material' && row.operator === 'ne'" class="hint">
              Matches at least one unequal value, even if another value equals this.
            </p>
            <p v-if="scope === 'material' && row.operator === 'is_not_set'" class="hint">
              Template attached, but no value stored for this key (including optional keys).
            </p>
          </template>
          <p v-if="rowError(row)" :id="`${idPrefix}-${row.uid}-error`" class="field-error" role="alert">{{ rowError(row) }}</p>
          <button type="button" class="link-button" :aria-label="`Remove ${scope} condition ${index + 1}`" @click="removeRow(index)">Remove</button>
        </div>
      </li>
    </ul>
    <button v-if="keyGroups.length" type="button" class="link-button" @click="addRow">+ Add {{ scope }} condition</button>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import { NO_VALUE_OPERATORS, OPERATOR_LABELS, newMetadataRow, validateMetadataRow } from '@/utils/experimentFilters.js';

export default {
  name: 'MetadataFilterEditor',
  components: { Multiselect },
  props: {
    idPrefix: { type: String, required: true },
    scope: { type: String, required: true },
    lists: { type: Array, default: () => [] },
    selectedLists: { type: Array, default: () => [] },
    rows: { type: Array, default: () => [] },
  },
  emits: ['change'],
  computed: {
    scopeLabel() { return this.scope.charAt(0).toUpperCase() + this.scope.slice(1); },
    availableLists() { return this.lists.filter((list) => list.keys?.length); },
    keyGroups() {
      const selected = new Set(this.selectedLists.map((list) => list.id));
      return this.availableLists.filter((list) => selected.has(list.id)).map((list) => ({
        listName: list.name,
        keys: list.keys.map((key) => ({ ...key, listId: list.id, listName: list.name })),
      }));
    },
  },
  methods: {
    change(rows, selectedLists = this.selectedLists) { this.$emit('change', { rows, selectedLists }); },
    onListsChange(lists) {
      const selected = new Set(lists.map((list) => list.id));
      this.change(this.rows.filter((row) => !row.key || selected.has(row.key.listId)), lists);
    },
    keyLabel(key) { return key.listName ? `${key.listName}: ${key.name}` : key.name; },
    operatorLabel(op) { return OPERATOR_LABELS[op] || op; },
    inputType(type) {
      return { integer: 'number', float: 'number', date: 'date', datetime: 'datetime-local' }[type] || 'text';
    },
    valueFields(row) {
      if (NO_VALUE_OPERATORS.includes(row.operator)) return [];
      return row.operator === 'between' ? ['value', 'value2'] : ['value'];
    },
    valueAria(row, field) {
      const label = row.operator === 'between' ? (field === 'value' ? 'Lower bound' : 'Upper bound') : 'Value';
      return `${this.scopeLabel} ${row.key.name}: ${label}`;
    },
    rowError(row) {
      const result = validateMetadataRow(row);
      return result.status === 'invalid' ? result.error : '';
    },
    addRow() { this.change([...this.rows, newMetadataRow()]); },
    removeRow(index) { this.change(this.rows.filter((_, i) => i !== index)); },
    updateRow(index, patch) { this.change(this.rows.map((row, i) => i === index ? { ...row, ...patch } : row)); },
    onKeyChange(index, key) {
      if (!key) return;
      const operator = key.operators.includes('eq') ? 'eq' : key.operators[0];
      this.updateRow(index, { key, operator, value: '', value2: '' });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";
.field-label {
  display: block;
  margin: 8px 0 4px;
  font-size: 0.85rem;
}

.hint,
.unit {
  font-size: 0.85rem;
  color: #555;
}

.hint { margin: 4px 0; }

.md-rows {
  list-style: none;
  margin: 8px 0;
  padding: 0;
}

.md-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: $global-bg-color;
  border: 1px solid $secondary-color;
  border-radius: 6px;
}

.and-label {
  display: block;
  margin: 6px 0;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: $primary-color;
}

.value-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

input,
select {
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #c8c8c0;
  border-radius: 5px;
  background: $form-color;

  &[aria-invalid="true"] { border-color: #b3261e; }
}

.field-error {
  margin: 0;
  font-size: 0.85rem;
  color: #b3261e;
}

.link-button {
  align-self: flex-start;
  padding: 2px 0;
  border: none;
  background: none;
  color: $primary-color;
  text-decoration: underline;
  cursor: pointer;
}

input:focus-visible,
select:focus-visible,
.link-button:focus-visible {
  outline: 2px solid $primary-color;
  outline-offset: 2px;
}
</style>